import { Col, Row, Stack } from 'react-bootstrap'
import './Upcoming.scss'

import React, { useMemo } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { useZocksAuth } from '../../components/ScopedBlock/hooks'
import { useGetMeQuery } from '../../store/user/user'
import { getIntegrationServiceIdsForCategory, IntegrationCategory, IntegrationServiceId } from '../../common/integrations'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { CalendarIntegrationTool } from '../../components/MeetingTools/CalendarIntegrationTool'
import { SharedPhoneTool } from '../../components/MeetingTools/SharedPhoneTool'
import { DedicatedPhoneIntegrationTool } from '../../components/MeetingTools/DedicatedPhoneIntegrationTool'
import { EmailIntegrationTool } from '../../components/MeetingTools/EmailIntegrationTool'
import { AdhocTool } from '../../components/MeetingTools/AdhocTool'
import { InPersonTool } from '../../components/MeetingTools/InPersonTool'
import { ReactComponent as LivePhoneIcon } from '../../assets/images/public-cloud-call-1.svg'
import { CollapsiblePanel } from '../../components/CollapsiblePanel/CollapsiblePanel'
import { useIntegrations } from '../Integration/useIntegrations'

export const UpcomingHeaderRow: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { data: userData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const { hasScope } = useZocksAuth()
  const hasStartSessionScope = hasScope('**', 'room_session:start', ['room_session:admin', 'room:admin'])
  const { isIntegrationAllowed, calendarIntegrationOptions } = useIntegrations()

  const hasCalendarIntegration = useMemo(() => {
    return !!(
      userData &&
      userData.integrations &&
      userData.integrations.some((i) => getIntegrationServiceIdsForCategory(IntegrationCategory.CALENDAR).includes(i.serviceId as IntegrationServiceId))
    )
  }, [userData])

  const hasPhoneIntegration = useMemo(() => {
    return !!(userData && userData.integrations && userData.integrations.some((i) => i.serviceId === IntegrationServiceId.PHONE && i.state === 'live'))
  }, [userData])

  const phoneConfig = useMemo(() => {
    if (!account?.data?.effectiveConfig?.integrations) {
      return null
    }
    return (
      (account.data.effectiveConfig.integrations.find(
        (integration) => integration.type === 'meeting_provider' && integration.serviceId === 'phone',
      ) as MeetingProviderAccountIntegrationConfig) ?? null
    )
  }, [account])

  const meetingTools = [
    ...(hasStartSessionScope ? [{ id: 'inPerson', icon: <i className='bi bi-laptop' />, body: <InPersonTool /> }] : []),
    ...(hasStartSessionScope && isIntegrationAllowed(IntegrationServiceId.PHONE) && phoneConfig?.sharedPhoneLineEnabled
      ? [{ id: 'sharedPhone', icon: <LivePhoneIcon width={'1rem'} height={'1rem'} className='me-1' />, body: <SharedPhoneTool phoneConfig={phoneConfig} /> }]
      : []),
    ...(hasStartSessionScope && isIntegrationAllowed(IntegrationServiceId.PHONE) && !phoneConfig?.sharedPhoneLineEnabled && !hasPhoneIntegration
      ? [{ id: 'dedicatedPhone', icon: <i className='bi bi-telephone' />, body: <DedicatedPhoneIntegrationTool /> }]
      : []),
    ...(hasStartSessionScope ? [{ id: 'email', icon: <i className='bi bi-robot' />, body: <EmailIntegrationTool /> }] : []),
    ...(!hasCalendarIntegration && calendarIntegrationOptions.length > 0
      ? [{ id: 'calendar', icon: <i className='bi bi-calendar2-week' />, body: <CalendarIntegrationTool /> }]
      : []),
    ...(hasStartSessionScope ? [{ id: 'adhoc', icon: <i className='bi bi-broadcast'></i>, body: <AdhocTool /> }] : []),
  ].slice(0, 3)

  return (
    <CollapsiblePanel
      id='UpcomingHeader-MeetingTools'
      defaultCollapsedState={'collapsed'}
      header={<h6>Meeting Tools</h6>}
      headerWhileCollapsed={
        <>
          <h6>Meeting Tools</h6>
          <Stack direction='horizontal' gap={1} className='tool-icons'>
            {meetingTools.map((tool) => (
              <div key={tool.id} className='meeting-tool-icon-wrapper'>
                {tool.icon}
              </div>
            ))}
          </Stack>
        </>
      }
    >
      <Row className='p-0'>
        {meetingTools.map((tool) => (
          <Col key={tool.id} xs={12} lg className='upcoming-integration-box'>
            {tool.body}
          </Col>
        ))}
      </Row>
    </CollapsiblePanel>
  )
}
